import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { dayLabelsColumn, monthLabelsColumn, yearDataColumn, yearLabelsColumn } from "../../../utils/data"
import { useTranslation } from "react-i18next"
import ColumnChart from "./ColumnChart"
import { radioButtonBarItems } from "../../../utils/design-helpers"
import dayjs from "dayjs"
import {
  getAllTimeColumnLabels,
  getDataAveragePerSession,
  getDateOfHigestUsage,
  getDatesOnChangedResolution,
  getFromAndToTimestampsFromColumnClick,
  getInitialTimeStamps,
  getTotalDataUsed,
  Resolution,
  RESOLUTIONS,
  usageDataToColumnAllTimeShared,
  usageDataToColumnDataDay,
  usageDataToColumnDataMonth,
  usageDataToColumnDataYearShared,
} from "../../../utils/chart-helpers"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { useSinglePlanById } from "../../../hooks/useSinglePlanById"
import Subtitle from "../../atoms/typography/Subtitle"

import DatePicker from "../../atoms/inputs/DatePicker"
import Checkbox from "../../atoms/inputs/Checkbox"
import StatisticSelectionChipButton from "../../molecules/StatisticSelectionChipButton"
import BarChartIcon from "../../atoms/icons/StatisticChipIcons/BarChartIcon"
import H4 from "../../atoms/typography/H4"
import PieChartIcon from "../../atoms/icons/StatisticChipIcons/PieChartIcon"
import MetricChip from "../../molecules/MetricChip"
import RadioButtonBar from "../../molecules/RadioButtonBar"
import Body from "../../atoms/typography/Body"
import ViewReportButton from "../../../reports/components/ViewReportButton"
import { DataPlanModel } from "../../../utils/models"
import { dataPlanReportOptions } from "../../../reports/reports-helpers"
import { GlobeIconLarge } from "../../atoms/icons/GlobeIcon"
import { captureExceptionWithContext } from "../../../utils/sentry"

interface DataPlanChartsViewProps {
  pieData?: any
  name?: string
  dataLastSynced?: string
  singlePlanApiId: number
  dataplan: DataPlanModel
  onClickNotificationSettings?: () => void
}

const DataPlanChartsView: React.FunctionComponent<DataPlanChartsViewProps> = ({
  name,
  dataLastSynced,
  singlePlanApiId,
  dataplan,
}) => {
  const { t } = useTranslation()
  const { now, fromTimestamp, toTimestamp } = getInitialTimeStamps()
  const [clickedSpan, setClickedSpan] = React.useState(false)
  const [statisticView, setStatisticView] = React.useState<"bar" | "pie" | "world">("bar")

  const [fromTime, setFromTime] = React.useState<any>(fromTimestamp)
  const [toTime, setToTime] = React.useState(dayjs().format("YYYY-MM-DDTHH:mm:ssZ"))

  const [selectedRadio, setSelectedRadio] = React.useState<Resolution>(RESOLUTIONS.year)

  const { singlePlan, error, loading } = useSinglePlanById(singlePlanApiId, selectedRadio, fromTime, toTime)

  const onSelectRadio = (spanId: Resolution) => {
    setSelectedRadio(spanId)
    const { from, to } = getDatesOnChangedResolution(fromTime, spanId, clickedSpan, singlePlan?.start, singlePlan?.end)

    //Adjust resolution based on view
    setFromTime(from)
    setToTime(to)
  }

  if (error) {
    captureExceptionWithContext(error, `error fetching single plan by id ${singlePlanApiId}`)
  }

  const sptum = singlePlan?.totalUsageMetrics ?? [] //single plan total usage metrics

  const terminalDataYear = usageDataToColumnDataYearShared(sptum)
  const terminalDataMonth = usageDataToColumnDataMonth(sptum, now)
  const terminalDataDay = usageDataToColumnDataDay(sptum)
  const terminalDataAllTime = usageDataToColumnAllTimeShared(sptum, singlePlan?.start, singlePlan?.end)

  const [columnData, setColumnData] = React.useState(yearDataColumn)
  const [columnLabels, setColumnLabels] = React.useState(yearLabelsColumn)

  React.useMemo(() => {
    if (selectedRadio === RESOLUTIONS.month) {
      setColumnData(terminalDataMonth ?? [])
      setColumnLabels(monthLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.day) {
      setColumnData(terminalDataDay ?? [])
      setColumnLabels(dayLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.allTime) {
      const allTimeLabels = getAllTimeColumnLabels(singlePlan?.start, singlePlan?.end)
      setColumnData(terminalDataAllTime ?? [])
      setColumnLabels(allTimeLabels)
    } else {
      setColumnData(terminalDataYear ?? [])
      setColumnLabels(yearLabelsColumn)
    }
  }, [singlePlan, selectedRadio])

  const onClickColumnInChart = (fromDate: any) => {
    if (selectedRadio === RESOLUTIONS.year) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.month)
    } else if (selectedRadio === RESOLUTIONS.month) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.day)
    } else if (selectedRadio === RESOLUTIONS.allTime) {
      const { from, to } = getFromAndToTimestampsFromColumnClick(fromDate, selectedRadio)
      setFromTime(from)
      setToTime(to)
      setSelectedRadio(RESOLUTIONS.year)
    } else {
      console.log("cannot click this column")
    }
  }

  const sessions = sptum.length
  const dateHighestUsed = getDateOfHigestUsage(sptum, selectedRadio)
  const avgSession = getDataAveragePerSession(sptum)
  const totalDataUsed = getTotalDataUsed(sptum)

  //console.log("From: ", dayjs(fromTime).format("DD MMM YYYY HH:mm"));
  //console.log("To: ", dayjs(toTime).format("DD MMM YYYY HH:mm"));

  const onSelectFromDate = (value: string) => {
    setFromTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onSelectToDate = (value: string) => {
    setToTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onCheckAllTime = () => {
    setSelectedRadio(RESOLUTIONS.allTime)
    setFromTime(dayjs(singlePlan?.start).format("YYYY-MM-DDTHH:mm:ssZ"))
    setToTime(dayjs(singlePlan?.end).format("YYYY-MM-DDTHH:mm:ssZ"))
  }

  return (
    <Container style={{ padding: "2rem 0.5rem" }}>
      <Row justify="center" align="center">
        <Subtitle textAlign="center">
          {name} {t("statistics")}
        </Subtitle>
        <Spacer spacing={1} />
        <ViewReportButton
          small
          options={dataPlanReportOptions(dataplan, {
            fromTimestamp: fromTime,
            toTimestamp: toTime,
            resolution: selectedRadio,
          })}
        />
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" style={{ padding: 15 }}>
        <Col lg={6}>
          <Row align="center">
            <Col lg={2} md={2}>
              <Hidden xs>
                <Body>Show by: </Body>
              </Hidden>
              <Visible xs>
                <HorizontalSpacer spacing={2} />
                <Body textAlign="center">Show by: </Body>
                <HorizontalSpacer spacing={1} />
              </Visible>
            </Col>
            <Col lg={6} md={6} xs={12}>
              <RadioButtonBar items={radioButtonBarItems} selectedValue={selectedRadio} onClickItem={onSelectRadio} />
            </Col>
          </Row>

          <HorizontalSpacer spacing={2} />
          <Visible md>
            <Body textAlign="center">Show statistics between: </Body>
          </Visible>
          <div style={{ display: "flex", alignItems: "center" }}>
            <DatePicker
              onSelectDate={onSelectFromDate}
              value={dayjs(fromTime).format("YYYY-MM-DD")}
              disabled={selectedRadio === RESOLUTIONS.allTime}
              useMinDate={false}
            />
            -
            <DatePicker
              onSelectDate={onSelectToDate}
              value={dayjs(toTime).format("YYYY-MM-DD")}
              disabled={selectedRadio === RESOLUTIONS.allTime}
              useMinDate={false}
            />
            <Hidden xs md>
              <Spacer spacing={1} />
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={selectedRadio === RESOLUTIONS.allTime} onChange={onCheckAllTime} />
                <Spacer spacing={1} /> All time
              </div>
            </Hidden>
          </div>
        </Col>
        <Hidden xs md>
          <Col lg={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <StatisticSelectionChipButton
              icon={<BarChartIcon />}
              title="Usage statistics"
              selected={statisticView === "bar"}
              onClick={() => setStatisticView("bar")}
            />
            <Spacer spacing={1} />
            <StatisticSelectionChipButton
              icon={<PieChartIcon />}
              title="Individual terminal statistics"
              selected={statisticView === "pie"}
              onClick={() => setStatisticView("pie")}
              disabled
            />
            <Spacer spacing={1} />

            <StatisticSelectionChipButton
              icon={<GlobeIconLarge />}
              title="Location statistics"
              selected={statisticView === "world"}
              onClick={() => setStatisticView("world")}
              disabled
            />
          </Col>
        </Hidden>
      </Row>
      <Hidden xs md>
        <HorizontalSpacer spacing={3} />
        <Row justify="around">
          <MetricChip value={t("graphs.times_used")} icon={<H4 textAlign="center">{sessions}</H4>} />
          <MetricChip value="Date of highest used" icon={<H4 textAlign="center">{dateHighestUsed}</H4>} />
          <MetricChip value="Data average per session" icon={<H4 textAlign="center">{avgSession}</H4>} />
          <MetricChip value="Total data used" icon={<H4 textAlign="center">{totalDataUsed}</H4>} />
        </Row>
      </Hidden>
      <HorizontalSpacer spacing={3} />
      <Hidden xs md>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <ColumnChart
            data={columnData}
            labels={columnLabels}
            columnWidth={24}
            onClickColumnInChart={onClickColumnInChart}
          />
        )}
      </Hidden>
      <Visible xs md>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <ColumnChart
            data={columnData}
            labels={columnLabels}
            columnWidth={16}
            onClickColumnInChart={onClickColumnInChart}
            width={500}
          />
        )}
      </Visible>

      {dataLastSynced && (
        <Body>
          {t("graphs.data_last_synced_at")} {dayjs(dataLastSynced).format("DD MMM YYYY - HH:mm:ss")}
        </Body>
      )}
    </Container>
  )
}

export default DataPlanChartsView
